import { type ComponentType } from "react";
import type { OfferData, OfferSectionProps, TemplateType } from "@/components/offer/offerDocumentTypes";
import { AssumptionsSection } from "@/components/offer/sections/AssumptionsSection";
import { CTASection } from "@/components/offer/sections/CTASection";
import { CoverSection } from "@/components/offer/sections/CoverSection";
import { EstimateTableSection } from "@/components/offer/sections/EstimateTableSection";
import { IncludedExcludedSection } from "@/components/offer/sections/IncludedExcludedSection";
import { MaterialsSection } from "@/components/offer/sections/MaterialsSection";
import { OfferHeaderSection } from "@/components/offer/sections/OfferHeaderSection";
import { PaymentTermsSection } from "@/components/offer/sections/PaymentTermsSection";
import { PriceSummarySection } from "@/components/offer/sections/PriceSummarySection";
import { ExclusionsSection } from "@/components/offer/sections/ExclusionsSection";
import { WarrantySection } from "@/components/offer/sections/WarrantySection";
import { TimelineSection } from "@/components/offer/sections/TimelineSection";

export type OfferSectionComponent = ComponentType<OfferSectionProps>;

export type OfferTemplateSectionConfig = {
  key: string;
  component: OfferSectionComponent;
  isVisible?: (offerData: OfferData) => boolean;
};

function warrantyVisible(offerData: OfferData): boolean {
  const value = offerData.warrantyType?.trim().toLowerCase();
  return (
    value !== "none" &&
    value !== "hidden" &&
    value !== "nie pokazuj"
  );
}

function materialsVisible(offerData: OfferData): boolean {
  return Boolean(offerData.materialsOwnership?.trim());
}

function paymentTermsVisible(offerData: OfferData): boolean {
  return Boolean(offerData.paymentTerms?.trim());
}

function exclusionsVisible(offerData: OfferData): boolean {
  return (offerData.exclusions?.some((item) => item.trim().length > 0) ?? false);
}

function timelineVisible(offerData: OfferData): boolean {
  return Boolean(offerData.offerTexts?.timelineNote?.trim());
}

function includedExcludedVisible(offerData: OfferData): boolean {
  return offerData.step3SectionIncludes?.includedExcluded !== false;
}

export const OFFER_TEMPLATE_CONFIGS: Record<TemplateType, OfferTemplateSectionConfig[]> = {
  quick: [
    { key: "offer-header", component: OfferHeaderSection },
    { key: "price-summary", component: PriceSummarySection },
    { key: "estimate-table", component: EstimateTableSection },
    { key: "included-excluded", component: IncludedExcludedSection, isVisible: includedExcludedVisible },
    { key: "materials", component: MaterialsSection, isVisible: materialsVisible },
    { key: "payment-terms", component: PaymentTermsSection, isVisible: paymentTermsVisible },
    { key: "warranty", component: WarrantySection, isVisible: warrantyVisible },
    { key: "exclusions", component: ExclusionsSection, isVisible: exclusionsVisible },
    { key: "cta", component: CTASection },
  ],
  explained: [
    { key: "offer-header", component: OfferHeaderSection },
    { key: "price-summary", component: PriceSummarySection },
    { key: "estimate-table", component: EstimateTableSection },
    { key: "materials", component: MaterialsSection, isVisible: materialsVisible },
    { key: "timeline", component: TimelineSection, isVisible: timelineVisible },
    { key: "assumptions", component: AssumptionsSection },
    { key: "included-excluded", component: IncludedExcludedSection, isVisible: includedExcludedVisible },
    { key: "payment-terms", component: PaymentTermsSection },
    { key: "warranty", component: WarrantySection, isVisible: warrantyVisible },
    { key: "exclusions", component: ExclusionsSection, isVisible: exclusionsVisible },
    { key: "cta", component: CTASection },
  ],
  premium: [
    { key: "cover", component: CoverSection },
    { key: "offer-header", component: OfferHeaderSection },
    { key: "timeline", component: TimelineSection, isVisible: timelineVisible },
    { key: "estimate-table", component: EstimateTableSection },
    { key: "price-summary", component: PriceSummarySection },
    { key: "materials", component: MaterialsSection, isVisible: materialsVisible },
    { key: "assumptions", component: AssumptionsSection },
    { key: "included-excluded", component: IncludedExcludedSection, isVisible: includedExcludedVisible },
    { key: "payment-terms", component: PaymentTermsSection, isVisible: paymentTermsVisible },
    { key: "warranty", component: WarrantySection, isVisible: warrantyVisible },
    { key: "exclusions", component: ExclusionsSection, isVisible: exclusionsVisible },
    { key: "cta", component: CTASection },
  ],
};

